/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { BuildingType, ResearchProject } from '../types';

interface BuildingTooltipContent {
  name: string;
  category: string;
  learning_objective: string;
  strategic_value: string;
  real_world_application: string;
  compliance_impact: string;
  progression_path: string;
  training_notes: string;
  awareness_points: string[];
}

interface ResearchTooltipContent {
  name: string;
  category: string;
  learning_objective: string;
  strategic_value: string;
  real_world_application: string;
  compliance_impact: string;
  progression_path: string;
  time_to_complete: string;
  training_notes: string;
  awareness_points: string[];
  prerequisites: string[];
  unlocks: string[];
  educational_resources: string[];
}

interface ProductTooltipContent {
  name: string;
  category: string;
  learning_objective: string;
  business_value: string;
  market_analysis: string;
  revenue_projection: string;
  compliance_requirements: string[];
  risk_factors: string[];
  real_world_application: string;
  educational_notes: string;
  success_metrics: string[];
  training_points: string[];
}

interface FAQContent {
  id: string;
  question: string;
  answer: string;
  educational_context?: string;
  related_buildings?: string[];
  practical_tips?: string[];
  revenue_examples?: string[];
  progression_path?: string;
  prevention_tips?: string[];
  scaling_benefits?: string[];
  regulatory_coverage?: string[];
  threat_management?: string[];
  building_examples?: string[];
  emergency_preparation?: string[];
  architecture_components?: string[];
  revenue_strategy?: string[];
  research_impact?: string[];
  victory_conditions?: string[];
  financial_strategies?: string[];
  product_requirements?: string;
  threat_reduction?: string[];
  connection_tips?: string[];
  keyboard_shortcuts?: Record<string, string>;
  getting_started_checklist?: string[];
  common_mistakes?: string[];
}

class TooltipContentService {
  private buildingTooltips: Record<string, BuildingTooltipContent> = {};
  private researchTooltips: Record<string, ResearchTooltipContent> = {};
  private productTooltips: Record<string, ProductTooltipContent> = {};
  private faqContent: Record<string, FAQContent> = {};
  private initialized = false;

  async initialize() {
    if (this.initialized) return;

    try {
      // Load building tooltips
      const buildingResponse = await fetch('/content-system/Building-Tooltips/all-buildings.json');
      if (buildingResponse.ok) {
        const buildingData = await buildingResponse.json();
        this.buildingTooltips = buildingData.buildings;
        console.log('✅ Building tooltips loaded:', Object.keys(this.buildingTooltips).length);
      } else {
        console.warn('⚠️ Could not load building tooltips, using fallbacks');
      }

      // Load research tooltips
      const researchResponse = await fetch('/content-system/Research-Tooltips/all-research.json');
      if (researchResponse.ok) {
        const researchData = await researchResponse.json();
        this.researchTooltips = researchData.research_projects;
        console.log('✅ Research tooltips loaded:', Object.keys(this.researchTooltips).length);
      } else {
        console.warn('⚠️ Could not load research tooltips, using fallbacks');
      }

      // Load product tooltips
      const productResponse = await fetch('/content-system/Product-Information/all-products.json');
      if (productResponse.ok) {
        const productData = await productResponse.json();
        this.productTooltips = productData.financial_products;
        console.log('✅ Product tooltips loaded:', Object.keys(this.productTooltips).length);
      } else {
        console.warn('⚠️ Could not load product tooltips, using fallbacks');
      }

      // Load FAQ content
      const faqResponse = await fetch('/content-system/FAQ-Help-System/gameplay-faq.json');
      if (faqResponse.ok) {
        const faqData = await faqResponse.json();
        // Flatten all FAQ questions into a single record for easy lookup
        Object.values(faqData.faq_categories).forEach(category => {
          category.questions.forEach((faq: FAQContent) => {
            this.faqContent[faq.id] = faq;
          });
        });
        console.log('✅ FAQ content loaded:', Object.keys(this.faqContent).length);
      } else {
        console.warn('⚠️ Could not load FAQ content, using fallbacks');
      }

      this.initialized = true;
    } catch (error) {
      console.error('❌ Error loading tooltip content:', error);
      this.initialized = true; // Still mark as initialized to prevent retry loops
    }
  }

  getBuildingTooltip(buildingType: BuildingType): BuildingTooltipContent | null {
    const key = `BuildingType.${buildingType}`;
    return this.buildingTooltips[key] || null;
  }

  getResearchTooltip(researchId: string): ResearchTooltipContent | null {
    return this.researchTooltips[researchId] || null;
  }

  getProductTooltip(productId: string): ProductTooltipContent | null {
    return this.productTooltips[productId] || null;
  }

  getFAQ(faqId: string): FAQContent | null {
    return this.faqContent[faqId] || null;
  }

  // Helper methods for formatted content
  getLearningObjective(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.learning_objective || 'Understanding fintech operational requirements';
  }

  getStrategicValue(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.strategic_value || 'Essential component of your fintech infrastructure';
  }

  getRealWorldApplication(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.real_world_application || 'Essential component in modern fintech architecture';
  }

  getComplianceImpact(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.compliance_impact || 'Contributes to overall regulatory compliance posture';
  }

  getProgressionPath(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.progression_path || 'Builds foundation for advanced fintech capabilities';
  }

  getTrainingNotes(buildingType: BuildingType): string {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.training_notes || 'Essential for fintech operations and compliance';
  }

  getAwarenessPoints(buildingType: BuildingType): string[] {
    const tooltip = this.getBuildingTooltip(buildingType);
    return tooltip?.awareness_points || ['Key learning objectives for this building type'];
  }

  // Research helper methods
  getResearchTimeToComplete(researchId: string): string {
    const tooltip = this.getResearchTooltip(researchId);
    return tooltip?.time_to_complete || '2-3 business days';
  }

  getResearchPrerequisites(researchId: string): string[] {
    const tooltip = this.getResearchTooltip(researchId);
    return tooltip?.prerequisites || [];
  }

  getResearchUnlocks(researchId: string): string[] {
    const tooltip = this.getResearchTooltip(researchId);
    return tooltip?.unlocks || [];
  }

  // Product helper methods
  getProductRevenueProjection(productId: string): string {
    const tooltip = this.getProductTooltip(productId);
    return tooltip?.revenue_projection || 'Revenue varies based on market conditions';
  }

  getProductMarketAnalysis(productId: string): string {
    const tooltip = this.getProductTooltip(productId);
    return tooltip?.market_analysis || 'Market analysis available in product dashboard';
  }

  getProductRiskFactors(productId: string): string[] {
    const tooltip = this.getProductTooltip(productId);
    return tooltip?.risk_factors || ['Market risks and regulatory considerations apply'];
  }

  // FAQ helper methods
  getQuickHelp(): {
    keyboard_shortcuts: Record<string, string>;
    getting_started_checklist: string[];
    common_mistakes: string[];
  } {
    const generalFAQ = this.faqContent['quick_help'] as any;
    return {
      keyboard_shortcuts: generalFAQ?.keyboard_shortcuts || {},
      getting_started_checklist: generalFAQ?.getting_started_checklist || [],
      common_mistakes: generalFAQ?.common_mistakes || []
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // Fallback content for when JSON files can't be loaded
  private getFallbackBuildingTooltip(buildingType: BuildingType): BuildingTooltipContent {
    return {
      name: buildingType,
      category: 'Infrastructure',
      learning_objective: 'Understanding fintech operational requirements',
      strategic_value: 'Essential component of your fintech infrastructure',
      real_world_application: 'Essential component in modern fintech architecture',
      compliance_impact: 'Contributes to overall regulatory compliance posture',
      progression_path: 'Builds foundation for advanced fintech capabilities',
      training_notes: 'Essential for fintech operations and compliance',
      awareness_points: ['Key learning objectives for this building type']
    };
  }
}

// Export singleton instance
export const tooltipContentService = new TooltipContentService();

// Export types for use in components
export type {
  BuildingTooltipContent,
  ResearchTooltipContent,
  ProductTooltipContent,
  FAQContent
};